import { PlanOption } from "@/features/planSelection/models/PlanOption.js";
import { PlanOptionDto } from "@/features/planSelection/dtos/PlanOptionDto.js";
import { PlanOptionGroup } from "@/features/planSelection/models/PlanOptionGroup.js";
import { planDto } from "@/features/planSelection/models/PlanDto.js";
import { GroupConfig } from "./GroupConfig";

// Build and export flat list and grouped view model
export function getPlanList(): PlanOption[] {
    return PLAN_DTO_LIST.map(PlanOption.fromDto);
}

export function getOptionGroups(): PlanOptionGroup[] {
    const all = getPlanList();

    return GROUP_CONFIGS.map(cfg => ({
        id: cfg.key,
        key: cfg.key,
        title: cfg.title,
        subtitle: cfg.subtitle,
        tooltip: cfg.tooltip,
        popularIds: cfg.popularIds,
        multiSelect: cfg.multiSelect,
        alwaysSelectedIds: cfg.alwaysSelectedIds,
        required: !!cfg.required,
        mutualExclusionGroups: cfg.mutualExclusionGroups,
        options: all.filter(p => p.groupKey === cfg.key),
    }));
}

const GROUP_CONFIGS: GroupConfig[] = [
    {
        key: 'buildLaunch',
        title: 'planSelection.group.buildLaunch.title',
        subtitle: 'planSelection.group.buildLaunch.subtitle',
        tooltip: 'planSelection.group.buildLaunch.tooltip',
        popularIds: ['webHosting_total'],
        multiSelect: true,
        required: true,
        mutualExclusionGroups: [
            ['webHosting_total', 'webHosting_pro'] // These hosting plans are mutually exclusive
        ],
    },
    {
        key: 'techConcierge',
        title: 'planSelection.group.techConcierge.title',
        subtitle: 'planSelection.group.techConcierge.subtitle',
        tooltip: 'planSelection.group.techConcierge.tooltip',
        popularIds: ['techConcierge_extra_5h'],
        alwaysSelectedIds: ['techConcierge_free'],
        multiSelect: true,
        required: false,
    },
    {
        key: 'growthOptimize',
        title: 'planSelection.group.growthOptimize.title',
        subtitle: 'planSelection.group.growthOptimize.subtitle',
        tooltip: 'planSelection.group.growthOptimize.tooltip',
        popularIds: ['seo_keyword'],
        multiSelect: true,
        required: false,
    },
    {
        key: 'visualIdentity',
        title: 'planSelection.group.visualIdentity.title',
        subtitle: 'planSelection.group.visualIdentity.subtitle',
        tooltip: 'planSelection.group.visualIdentity.tooltip',
        popularIds: ['branding_full'],
        multiSelect: false,
        required: false,
    }
];

const BUILD_LAUNCH_PLANS: PlanOptionDto[] = [
    planDto({
        id: 'webHosting_total',
        whmcsProductId: 16,
        groupKey: 'buildLaunch',
        displayName: 'plans.webHosting_total.displayName',
        features: [
            'plans.webHosting_total.features.0',
            'plans.webHosting_total.features.1',
            'plans.webHosting_total.features.2',
            'plans.webHosting_total.features.3',
            'plans.webHosting_total.features.4',
            'plans.webHosting_total.features.5',
        ],
        price: 30,
        billingLabel: 'monthly',
        link: '/signup/web-hosting/standard',
        rationale: 'plans.webHosting_total.rationale',
        isPopular: false
    }),
    planDto({
        id: 'webHosting_pro',
        whmcsProductId: 17,
        groupKey: 'buildLaunch',
        displayName: 'plans.webHosting_pro.displayName',
        features: [
            'plans.webHosting_pro.features.0',
            'plans.webHosting_pro.features.1',
            'plans.webHosting_pro.features.2'
        ],
        price: 50,
        billingLabel: 'monthly',
        link: '/signup/web-hosting/pro',
        rationale: 'plans.webHosting_pro.rationale',
        isPopular: false
    }),
    planDto({
        id: 'domain_managed',
        whmcsProductId: 99,
        groupKey: 'buildLaunch',
        displayName: 'plans.domain_managed.displayName',
        features: [
            'plans.domain_managed.features.0',
            'plans.domain_managed.features.1',
        ],
        price: 15,
        billingLabel: 'yearly',
        link: '/signup/domain/managed',
        rationale: 'plans.domain_managed.rationale',
        isPopular: false,
    })
];


const TECH_CONCIERGE_PLANS: PlanOptionDto[] = [
    planDto({
        id: 'techConcierge_free',
        whmcsProductId: 19,
        groupKey: 'techConcierge',
        displayName: 'plans.techConcierge_free.displayName',
        features: [
            'plans.techConcierge_free.features.0',
            'plans.techConcierge_free.features.1',
            'plans.techConcierge_free.features.2',
            'plans.techConcierge_free.features.3'
        ],
        price: 0,
        billingLabel: 'monthly',
        link: '/signup/tech-concierge/free',
        rationale: 'plans.techConcierge_free.rationale',
        isPopular: false
    }),
    planDto({
        id: 'techConcierge_extra_5h',
        whmcsProductId: 22,
        groupKey: 'techConcierge',
        displayName: 'plans.techConcierge_extra_5h.displayName',
        features: [
            'plans.techConcierge_extra_5h.features.0',
            'plans.techConcierge_extra_5h.features.1',
            'plans.techConcierge_extra_5h.features.2'
        ],
        price: 300,
        billingLabel: 'oneTime',
        link: '/signup/tech-concierge/2h',
        rationale: 'plans.techConcierge_extra_5h.rationale',
        isPopular: false
    }),
    planDto({
        id: 'techConcierge_5h',
        whmcsProductId: 21,
        groupKey: 'techConcierge',
        displayName: 'plans.techConcierge_5h.displayName',
        features: [
            'plans.techConcierge_5h.features.0',
            'plans.techConcierge_5h.features.1',
            'plans.techConcierge_5h.features.2',
            'plans.techConcierge_5h.features.3',
            'plans.techConcierge_5h.features.4',
            'plans.techConcierge_5h.features.5'
        ],
        price: 450,
        billingLabel: 'oneTime',
        link: '/signup/tech-concierge/5h',
        rationale: 'plans.techConcierge_5h.rationale',
        isPopular: false
    })
];


const GROWTH_OPTIMIZED_PLANS: PlanOptionDto[] = [
    planDto({
        id: 'growthCall_monthly',
        whmcsProductId: 31,
        groupKey: 'growthOptimize',
        displayName: 'plans.growthCall_monthly.displayName',
        features: [
            'plans.growthCall_monthly.features.0',
            'plans.growthCall_monthly.features.1',
            'plans.growthCall_monthly.features.2',
            'plans.growthCall_monthly.features.3',
            'plans.growthCall_monthly.features.4',
            'plans.growthCall_monthly.features.5'
        ],
        price: 89,
        billingLabel: 'monthly',
        link: '/signup/growth-call/monthly',
        rationale: 'plans.growthCall_monthly.rationale',
        isPopular: true
    }),

    planDto({
        id: 'humanContent_business',
        whmcsProductId: 32,
        groupKey: 'growthOptimize',
        displayName: 'plans.humanContent_business.displayName',
        features: [
            'plans.humanContent_business.features.0',
            'plans.humanContent_business.features.1',
            'plans.humanContent_business.features.2',
            'plans.humanContent_business.features.3',
            'plans.humanContent_business.features.4',
            'plans.humanContent_business.features.5'
        ],
        price: 450,
        billingLabel: 'monthly',
        link: '/signup/human-content/business',
        rationale: 'plans.humanContent_business.rationale',
        isPopular: false
    }),

    planDto({
        id: 'seo_booster',
        whmcsProductId: 33,
        groupKey: 'growthOptimize',
        displayName: 'plans.seo_booster.displayName',
        features: [
            'plans.seo_booster.features.0',
            'plans.seo_booster.features.1',
            'plans.seo_booster.features.2',
            'plans.seo_booster.features.3',
            'plans.seo_booster.features.4',
            'plans.seo_booster.features.5',
            'plans.seo_booster.features.6'
        ],
        price: 890,
        billingLabel: 'oneTime',
        link: '/signup/seo/booster',
        rationale: 'plans.seo_booster.rationale',
        isPopular: true
    }),

    planDto({
        id: 'cro_booster',
        whmcsProductId: 34,
        groupKey: 'growthOptimize',
        displayName: 'plans.cro_booster.displayName',
        features: [
            'plans.cro_booster.features.0',
            'plans.cro_booster.features.1',
            'plans.cro_booster.features.2',
            'plans.cro_booster.features.3',
            'plans.cro_booster.features.4',
            'plans.cro_booster.features.5',
            'plans.cro_booster.features.6',
            'plans.cro_booster.features.7'
        ],
        price: 1290,
        billingLabel: 'oneTime',
        link: '/signup/cro/accelerator',
        rationale: 'plans.cro_booster.rationale',
        isPopular: false
    }),

    planDto({
        id: 'content_strategy',
        whmcsProductId: 35,
        groupKey: 'growthOptimize',
        displayName: 'plans.content_strategy.displayName',
        features: [
            'plans.content_strategy.features.0',
            'plans.content_strategy.features.1',
            'plans.content_strategy.features.2',
            'plans.content_strategy.features.3',
            'plans.content_strategy.features.4',
            'plans.content_strategy.features.5',
            'plans.content_strategy.features.6',
            'plans.content_strategy.features.7'
        ],
        price: 2490,
        billingLabel: 'oneTime',
        link: '/signup/content/strategy-intensive',
        rationale: 'plans.content_strategy.rationale',
        isPopular: false
    })
];

const VISUAL_IDENTITY_PLANS: PlanOptionDto[] = [
    planDto({
        id: 'branding_mini',
        whmcsProductId: 36,
        groupKey: 'visualIdentity',
        displayName: 'plans.branding_mini.displayName',
        features: [
            'plans.branding_mini.features.0',
            'plans.branding_mini.features.1',
            'plans.branding_mini.features.2',
            'plans.branding_mini.features.3',
            'plans.branding_mini.features.4',
            'plans.branding_mini.features.5',
            'plans.branding_mini.features.6'
        ],
        price: 490,
        billingLabel: 'oneTime',
        link: '/signup/branding/mini-refresh',
        rationale: 'plans.branding_mini.rationale',
        isPopular: false
    }),

    planDto({
        id: 'branding_full',
        whmcsProductId: 37,
        groupKey: 'visualIdentity',
        displayName: 'plans.branding_full.displayName',
        features: [
            'plans.branding_full.features.0',
            'plans.branding_full.features.1',
            'plans.branding_full.features.2',
            'plans.branding_full.features.3',
            'plans.branding_full.features.4',
            'plans.branding_full.features.5',
            'plans.branding_full.features.6',
            'plans.branding_full.features.7',
            'plans.branding_full.features.8',
            'plans.branding_full.features.9'
        ],
        price: 790,
        billingLabel: 'oneTime',
        link: '/signup/branding/full-evolution',
        rationale: 'plans.branding_full.rationale',
        isPopular: true
    }),

    planDto({
        id: 'branding_premium',
        whmcsProductId: 38,
        groupKey: 'visualIdentity',
        displayName: 'plans.branding_premium.displayName',
        features: [
            'plans.branding_premium.features.0',
            'plans.branding_premium.features.1',
            'plans.branding_premium.features.2',
            'plans.branding_premium.features.3',
            'plans.branding_premium.features.4',
            'plans.branding_premium.features.5',
            'plans.branding_premium.features.6',
            'plans.branding_premium.features.7',
            'plans.branding_premium.features.8',
            'plans.branding_premium.features.9',
            'plans.branding_premium.features.10'
        ],
        price: 1490,
        billingLabel: 'oneTime',
        link: '/signup/branding/signature-experience',
        rationale: 'plans.branding_premium.rationale',
        isPopular: false
    }),

    planDto({
        id: 'brand_audit',
        whmcsProductId: 39,
        groupKey: 'visualIdentity',
        displayName: 'plans.brand_audit.displayName',
        features: [
            'plans.brand_audit.features.0',
            'plans.brand_audit.features.1',
            'plans.brand_audit.features.2',
            'plans.brand_audit.features.3',
            'plans.brand_audit.features.4',
            'plans.brand_audit.features.5',
            'plans.brand_audit.features.6',
            'plans.brand_audit.features.7'
        ],
        price: 350,
        billingLabel: 'oneTime',
        link: '/signup/branding/health-check',
        rationale: 'plans.brand_audit.rationale',
        isPopular: false
    })
];

// Combined Plan DTO List
const PLAN_DTO_LIST: PlanOptionDto[] = [
    ...BUILD_LAUNCH_PLANS,
    ...TECH_CONCIERGE_PLANS,
    ...GROWTH_OPTIMIZED_PLANS,
    ...VISUAL_IDENTITY_PLANS,
];